/**
 * StandupTab Component
 * AIDLC Administrative Backend
 *
 * Daily standup view for a project. Shows per-agent entries for a selected date
 * with auto-generate capability from agent activity data.
 */

import { useState, useEffect, useCallback } from 'react';
import { LoadingSpinner } from '../../shared/LoadingSpinner';
import { EmptyState } from '../../shared/EmptyState';
import { StandupAgentRow } from '../standup/StandupAgentRow';
import { StandupGenerateButton } from '../standup/StandupGenerateButton';
import { fetchStandups } from '../../../services/standupService';
import type { StandupEntry } from '../../../types/projects';

interface StandupTabProps {
  projectId: string;
  assignedAgents: string[];
}

function getAgentLabel(agentId: string): string {
  return agentId
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function StandupTab({ projectId, assignedAgents }: StandupTabProps) {
  const today = new Date().toISOString().split('T')[0];
  const [selectedDate, setSelectedDate] = useState(today);
  const [entries, setEntries] = useState<StandupEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStandups = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchStandups(projectId, selectedDate);
      setEntries(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load standups');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, selectedDate]);

  useEffect(() => {
    loadStandups();
  }, [loadStandups]);

  const handleGenerated = (newEntries: StandupEntry[]) => {
    setEntries(newEntries);
  };

  // Navigate date
  const goToDate = (offset: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + offset);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const formattedDate = new Date(selectedDate + 'T12:00:00').toLocaleDateString([], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div>
      {/* Date picker toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-zinc-500">Daily Standup</h3>
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
            <button
              onClick={() => goToDate(-1)}
              className="px-2 py-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
              title="Previous day"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="text-xs border-0 px-2 py-1.5 focus:outline-none focus:ring-0 bg-transparent"
            />
            <button
              onClick={() => goToDate(1)}
              className="px-2 py-1.5 text-gray-500 hover:bg-gray-50 transition-colors"
              title="Next day"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
          <span className="text-xs text-gray-500">{formattedDate}</span>
        </div>

        <StandupGenerateButton projectId={projectId} onGenerated={handleGenerated} />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
          <p className="text-red-700 text-xs">{error}</p>
          <button onClick={() => setError(null)} className="text-red-600 text-xs underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Standup entries */}
      {isLoading ? (
        <LoadingSpinner message="Loading standup data..." />
      ) : entries.length === 0 ? (
        <EmptyState
          title="No standup entries"
          description={`No standup data for ${formattedDate}. Click "Generate Standup" to auto-generate from agent activity.`}
        />
      ) : (
        <div className="space-y-3">
          {entries.map((entry) => (
            <StandupAgentRow
              key={entry.id}
              entry={entry}
              agentName={getAgentLabel(entry.agentId)}
            />
          ))}

          {/* Show agents with no entries */}
          {assignedAgents
            .filter((agentId) => !entries.find((e) => e.agentId === agentId))
            .map((agentId) => (
              <div
                key={agentId}
                className="bg-gray-50 border border-zinc-200 rounded-lg p-3 dark:bg-zinc-800 dark:border-zinc-700"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 text-gray-500 flex items-center justify-center text-xs font-bold">
                    {agentId.split('-').map((p) => p.charAt(0).toUpperCase()).slice(0, 2).join('')}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-600">{getAgentLabel(agentId)}</div>
                    <div className="text-[10px] text-gray-400 italic">No standup entry for this date</div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
